import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AmplitudeService } from '@inyova/inyova-shared';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-savings-deposit',
  styleUrls: ['./savings-deposit.component.scss'],
  templateUrl: './savings-deposit.component.html',
  standalone: false
})
export class SavingsDepositComponent implements OnInit, OnChanges, OnDestroy {
  @Input() currency: string;
  @Input() appLocation: string;
  @Input() language: string;
  @Input() newSavings: number;
  @Input() kind: '3b' | '3a' | 'kid' | 'ci' | 'grow';
  @Output() changeValue: EventEmitter<number | null> = new EventEmitter<number | null>();
  @Output() setDeposit: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('amountInput') amountInput: ElementRef;

  depositForm: FormGroup;
  invested: number;
  numRegex = /^-?\d*[.,]?\d{0,2}$/;
  showPlaceholder = true;
  maxLength = 9;
  year = new Date().getFullYear();
  protected readonly onDestroy$ = new Subject<void>();

  get assetControl(): AbstractControl {
    return this.depositForm?.get('asset');
  }

  get roundedMonthlySavings() {
    return Number(this.newSavings?.toFixed(1));
  }

  constructor(
    private fb: FormBuilder,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit() {
    this.depositForm = this.initializeForm();

    this.assetControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.onDestroy$))
      .subscribe((value: undefined | null | number) => {
        if (!(typeof value === 'undefined' || value === null)) {
          if (value < 0) {
            this.assetControl.patchValue(null, { onlySelf: true, emitEvent: false });
            this.assetControl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
            return;
          }
          if (value.toString().length > this.maxLength) {
            // eslint-disable-next-line radix
            this.assetControl.patchValue(parseInt(value.toString().slice(0, this.maxLength), 10), { onlySelf: true, emitEvent: false });
            this.assetControl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
            return;
          }
        }
        this.changeValue.emit(value);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['newSavings'] && !changes['newSavings'].isFirstChange()) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.updateForm(this.roundedMonthlySavings);
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  initializeForm(): FormGroup {
    return this.fb.group({
      asset: [this.roundedMonthlySavings, [Validators.required, Validators.pattern(this.numRegex)]]
    });
  }

  updateForm(value: number, emitEvent = false) {
    this.assetControl?.patchValue(value, { onlySelf: true, emitEvent });
    this.assetControl?.updateValueAndValidity({ onlySelf: true, emitEvent });
  }

  onSelectInput() {
    this.showPlaceholder = false;
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.amountInput.nativeElement.focus();
    });
  }

  onResetDefault() {
    this.updateForm(this.roundedMonthlySavings, true);
    this.showPlaceholder = true;
  }

  // eslint-disable-next-line consistent-return
  onSetDeposit(): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.amplitudeService.trackEvent(`Savings Plan Engaged`, { savings_plan_ctas: 'savings_plan_saved', kind: this.kind });

    this.setDeposit.emit(this.assetControl?.value || 0);
    setTimeout(() => {
      this.showPlaceholder = true;
    }, 1000);
  }
}
